// app/components/BuySell.tsx
'use client'

import React, { useState, useEffect, useCallback, memo } from 'react'
import styles from './BuySell.module.css'
import { usePathname } from 'next/navigation'
import { ConnectorManager } from '../ConnectorManager'
import type { GTTOrder, TickerItem } from '../types'
// import { useZerodhaWebSocket } from './hooks/useZerodhaWebSocket'
import { SymbolSearchModal } from '../../SymbolSearch/SymbolSearch'

/**
 * @uiComponent
 * @name BuySell
 * @description The BuySell component allows users to place buy and sell orders on the Zerodha platform.
 * It supports various order types including market, limit, stop loss, and GTT (Good Till Triggered) orders.
 * Features real-time price fetching, symbol selection, and comprehensive order validation.
 * @location Right side panel of the trading interface
 * @prop symbol The ticker symbol for the security being traded
 * @prop initialPrice Optional initial price for limit orders
 * @prop initialAction Optional initial action (BUY/SELL) to preset the interface
 */

/**
 * @uiWorkflow
 * @name Place a Market Order on Zerodha
 * @description How to quickly place a market order for immediate execution
 * @platform zerodha
 * @step 1 Navigate to the trading terminal and select your desired stock symbol [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel on the right side, ensure "Market" tab is selected [component: BuySell] [action: click]
 * @step 3 Enter the quantity you want to trade [component: BuySell] [action: input]
 * @step 4 Choose whether you want to buy or sell [component: BuySell] [action: select]
 * @step 5 Review the current market price displayed [component: BuySell] [action: observe]
 * @step 6 Click the "Buy" or "Sell" button to place your market order [component: BuySell] [action: click]
 * @step 7 Confirm the order in the confirmation dialog [component: OrderConfirmation] [action: click]
 */

/**
 * @uiWorkflow
 * @name Place a Limit Order on Zerodha
 * @description How to place a limit order with a specific price target
 * @platform zerodha
 * @step 1 Navigate to the trading terminal and select your stock [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel, click on the "Limit" tab [component: BuySell] [action: click]
 * @step 3 Enter your desired limit price [component: BuySell] [action: input]
 * @step 4 Enter the quantity you want to trade [component: BuySell] [action: input]
 * @step 5 Select Buy or Sell based on your trading intention [component: BuySell] [action: select]
 * @step 6 Review the price vs current market price [component: BuySell] [action: observe]
 * @step 7 Click the appropriate button to place your limit order [component: BuySell] [action: click]
 * @step 8 Confirm the order details in the popup [component: OrderConfirmation] [action: click]
 */

/**
 * @uiWorkflow
 * @name Place a Stop Loss Order on Zerodha
 * @description How to place a stop loss order to protect against downside risk
 * @platform zerodha
 * @step 1 Navigate to the trading terminal and select your desired stock symbol [component: SymbolPage] [action: navigate]
 * @step 2 In the Buy/Sell panel on the right side, choose whether you want to buy or sell [component: BuySell] [action: select]
 * @step 3 Select "SL" (Stop Loss) from the order type options [component: BuySell] [action: select]
 * @step 4 Enter the trigger price at which your order will be activated [component: BuySell] [action: input] [notes: For a sell stop-loss, set this below current market price]
 * @step 5 Enter the limit price at which you want to execute once triggered [component: BuySell] [action: input] [notes: This should be at or below the trigger price for a sell order]
 * @step 6 Enter the quantity you want to trade [component: BuySell] [action: input]
 * @step 7 Review your order details and click the "Sell" button [component: BuySell] [action: click]
 * @step 8 In the confirmation dialog, click "Confirm" to place your stop loss order [component: OrderConfirmation] [action: click]
 */

/**
 * @uiWorkflow
 * @name Set Up GTT (Good Till Triggered) Order on Zerodha
 * @description How to place a GTT order that remains active until triggered
 * @platform zerodha
 * @step 1 Navigate to your desired stock in the trading terminal [component: SymbolPage] [action: navigate]
 * @step 2 Click on the "GTT" tab in the Buy/Sell panel [component: BuySell] [action: click]
 * @step 3 Review the current market price displayed [component: BuySell] [action: observe]
 * @step 4 Enter the trigger price for your GTT order [component: BuySell] [action: input]
 * @step 5 Use the percentage buttons (+1%, +5%, +10%) for quick trigger price calculation [component: BuySell] [action: click]
 * @step 6 Enter the execution price once triggered [component: BuySell] [action: input]
 * @step 7 Specify the quantity for the order [component: BuySell] [action: input]
 * @step 8 Select trigger type (SINGLE or OCO) if applicable [component: BuySell] [action: select]
 * @step 9 Choose product type (CNC, MIS, NRML) [component: BuySell] [action: select]
 * @step 10 Click Buy or Sell to place your GTT order [component: BuySell] [action: click]
 * @step 11 Confirm the GTT order details [component: OrderConfirmation] [action: click]
 */

/**
 * @uiWorkflow
 * @name Use Symbol Search in Zerodha BuySell Panel
 * @description How to search and select different stocks for trading
 * @platform zerodha
 * @step 1 Locate the symbol selector in the Buy/Sell panel header [component: BuySell] [action: observe]
 * @step 2 Click on the current symbol dropdown button [component: BuySell] [action: click]
 * @step 3 The symbol search modal will open [component: SymbolSearchModal] [action: observe]
 * @step 4 Type the stock name or symbol you want to trade [component: SymbolSearchModal] [action: input]
 * @step 5 Select from the filtered results or popular symbols [component: SymbolSearchModal] [action: click]
 * @step 6 The symbol will update in the Buy/Sell panel [component: BuySell] [action: observe]
 * @step 7 Current market price will automatically fetch for the new symbol [component: BuySell] [action: observe]
 * @step 8 Proceed with placing your order for the selected symbol [component: BuySell] [action: continue]
 */

interface BuySellProps {
  symbol?: string
  initialPrice?: string
  initialAction?: 'BUY' | 'SELL' | null
}

const TABS: string[] = ['Market', 'Limit', 'SL', 'SL-M', 'GTT']


const BuySellComponent = ({ symbol: propSymbol, initialPrice = '', initialAction = null }: BuySellProps) => {
  const pathname = usePathname()
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'YESBANK.NS'

  // If the symbol ends with .NS or .BS, use a fallback symbol
  const defaultSymbol =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? symbolFromPath
      : 'paytm.ns'

  // State for symbol selection and custom input
  const [currentSymbol, setCurrentSymbol] = useState(propSymbol || defaultSymbol)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'PAYTM.NS',
    'YESBANK.NS',
    'STEELXIND.NS',
    'ICICIBANK.BS',
    'SWIGGY.BS'
  ])
  
  const [activeTab, setActiveTab] = useState<string>(initialPrice ? 'Limit' : 'Market')
  const [price, setPrice] = useState(initialPrice)
  const [triggerPrice, setTriggerPrice] = useState('')
  const [amount, setAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [orderStatus, setOrderStatus] = useState<string | null>(null)
  const [triggerType, setTriggerType] = useState<'SINGLE' | 'OCO'>('SINGLE')
  const [product, setProduct] = useState<'CNC' | 'MIS' | 'NRML'>('CNC')
  const [lastPrice, setLastPrice] = useState<number | null>(null)
  const [percentageValue, setPercentageValue] = useState<number>(5)
  const [tickerData, setTickerData] = useState<TickerItem | null>(null)

  const handleSymbolChange = useCallback((newSymbol: string) => {
    if (newSymbol) {
      const formattedSymbol = newSymbol.toUpperCase()
      setCurrentSymbol(formattedSymbol)
      setIsSymbolPopupOpen(false)
      setSymbolInput('')
    }
  }, [])



  // Function to fetch ticker data using getTickers with specific symbol parameter
  // Wrap fetchTickerData in useCallback to prevent unnecessary recreation
  const fetchTickerData = useCallback(async () => {
    try {
      const connectorManager = ConnectorManager.getInstance();
      const connector = connectorManager.getConnector('zerodha');
      
      if (connector && typeof connector.getTickers === 'function') {
        // Pass the current symbol to the API to get specific ticker data
        // This uses the enhanced API route that accepts a symbol parameter
        const tickers = await connector.getTickers({
          symbol: currentSymbol // Pass the current symbol to get specific data
        });
        console.log('Fetched ticker for:', currentSymbol, tickers);
        
        // Since we're requesting a specific symbol, we should get a direct match
        // But still handle the case where we might get multiple results
        let matchedTicker = null;
        
        if (tickers && tickers.length > 0) {
          // Try to find an exact match first
          matchedTicker = tickers.find(ticker => 
            ticker.symbol.toUpperCase() === currentSymbol.toUpperCase()
          );
          
          // If no exact match, use the first ticker returned
          if (!matchedTicker) {
            matchedTicker = tickers[0];
          }
          
          if (matchedTicker) {
            console.log('Found ticker data:', matchedTicker);
            setTickerData(matchedTicker);
            setLastPrice(matchedTicker.price);
            
            // If in GTT tab and trigger price is empty, set based on real-time price
            if (activeTab === 'GTT' && !triggerPrice && matchedTicker.price) {
              const calculatedTriggerPrice = (matchedTicker.price * (1 + percentageValue / 100)).toFixed(2);
              setTriggerPrice(calculatedTriggerPrice);
            }
          }
        } else {
          console.log('No ticker data returned for symbol:', currentSymbol);
        }
      }
    } catch (error) {
      console.error('Failed to fetch ticker data:', error);
    }
  // Dependencies for useCallback: ensure it updates when these values change
  }, [activeTab, currentSymbol, percentageValue, triggerPrice]);
  
  // Fetch ticker data on component mount and when symbol changes
  useEffect(() => {
    // Initial fetch
    fetchTickerData();
    
    // Set up polling interval (every 5 seconds)
    const intervalId = setInterval(() => {
      fetchTickerData();
    }, 5000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [currentSymbol, fetchTickerData]);
  
  // Additional fetch when tab changes to GTT
  useEffect(() => {
    if (activeTab === 'GTT') {
      fetchTickerData();
    }
  }, [activeTab, fetchTickerData])
  
  // Update trigger price when tab changes or symbol changes
  useEffect(() => {
    if (activeTab === 'GTT' && lastPrice && !triggerPrice) {
      // For BUY orders, set trigger price above current price
      // Default to BUY direction for initial setup
      const calculatedTriggerPrice = (lastPrice * (1 + percentageValue / 100)).toFixed(2);
      setTriggerPrice(calculatedTriggerPrice);
    }
  }, [activeTab, currentSymbol, lastPrice]);
  
  // Update trigger price when percentage changes
  const updateTriggerPriceFromPercentage = (percent: number, direction: 'BUY' | 'SELL' = 'BUY') => {
    if (lastPrice) {
      // For BUY GTT orders, trigger price should be above current price
      // For SELL GTT orders, trigger price should be below current price
      const multiplier = direction === 'BUY' ? (1 + percent / 100) : (1 - percent / 100);
      const calculatedTriggerPrice = (lastPrice * multiplier).toFixed(2);
      setTriggerPrice(calculatedTriggerPrice);
      setPercentageValue(percent);
    }
  };

  const placeOrder = useCallback(async (side: 'BUY' | 'SELL') => {
    // Validate required fields based on order type
    if (!amount) {
      setOrderStatus('Please enter an amount')
      return
    }
    
    if (activeTab === 'Limit' && !price) {
      setOrderStatus('Please enter a limit price')
      return
    }
    
    if ((activeTab === 'SL' || activeTab === 'SL-M') && !triggerPrice) {
      setOrderStatus('Please enter a trigger price')
      return
    }
    
    // For SL orders, price is also required
    if (activeTab === 'SL' && !price) {
      setOrderStatus('Please enter a limit price')
      return
    }
    
    // For GTT orders, trigger price is required
    if (activeTab === 'GTT' && !triggerPrice) {
      setOrderStatus('Please enter a trigger price')
      return
    }

    setIsLoading(true)
    setOrderStatus('Placing order...')
    
    // Fetch latest ticker data before placing order
    if (activeTab === 'GTT') {
      await fetchTickerData()
    }

    try {
      // Get the connector manager instance
      const connectorManager = ConnectorManager.getInstance()
      
      // Get the Zerodha connector
      const connector = connectorManager.getConnector('zerodha')
      
      if (!connector) {
        throw new Error('Zerodha connector not found. Please connect to Zerodha first.')
      }

      // Extract exchange information from symbol suffix and format the symbol
      let exchange = ''
      const formattedSymbol = currentSymbol.replace(/\.(NS|BS)$/i, (match, group) => {
        exchange = group.toUpperCase() === 'NS' ? 'NSE' : 'BSE'
        return ''
      })
      
      if (activeTab === 'GTT') {
        // For GTT orders
        if (!exchange) {
          throw new Error('Exchange information is required for GTT orders. Use format like RELIANCE.NS')
        }
        
        // Prepare the GTT order object
        const gttOrder: Partial<GTTOrder> = {
          symbol: formattedSymbol,
          quantity: parseFloat(amount),
          price: parseFloat(price) || parseFloat(triggerPrice), // Default to trigger price if price not specified
          type: 'GTT',
          side: side,
          exchange: exchange,
          triggerPrice: parseFloat(triggerPrice),
          triggerType: triggerType,
          product: product,
          // Always fetch the latest price data before placing GTT order
          lastPrice: lastPrice || undefined
        }
        
        // Place the GTT order
        if (typeof connector.placeGTT === 'function') {
          const result = await connector.placeGTT(gttOrder)
          setOrderStatus(`GTT order placed successfully: ${result.orderId || 'Order submitted'}`)
        } else {
          throw new Error('GTT orders are not supported by this connector')
        }
      } else {
        // For regular orders
        const order = {
          symbol: formattedSymbol,
          quantity: parseFloat(amount),
          price: ['Limit', 'SL'].includes(activeTab) ? parseFloat(price) : undefined,
          type: activeTab.toUpperCase() as 'MARKET' | 'LIMIT' | 'SL' | 'SL-M',
          side: side,
          exchange: exchange || undefined, // Include exchange info if available
          triggerPrice: ['SL', 'SL-M'].includes(activeTab) ? parseFloat(triggerPrice) : undefined
        }

        // Place the order
        const result = await connector.placeOrder(order)
        setOrderStatus(`Order placed successfully: ${result.orderId || 'Order submitted'}`)
      }
      
      // Reset form fields
      setAmount('')
      if (['Limit', 'SL', 'GTT'].includes(activeTab)) {
        setPrice('')
      }
      if (['SL', 'SL-M', 'GTT'].includes(activeTab)) {
        setTriggerPrice('')
      }
      if (activeTab === 'GTT') {
        setTriggerType('SINGLE')
        setProduct('CNC')
      }
    } catch (error) {
      console.error('Order placement failed:', error)
      
      // Extract and format error message from API response
      let errorMessage = 'Unknown error';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // Check for specific GTT error messages
        if (errorMessage.includes('Trigger already met')) {
          errorMessage = 'Trigger price has already been met. For BUY orders, trigger price must be above current price. For SELL orders, trigger price must be below current price.';
        }
      }
      
      setOrderStatus(`Order failed: ${errorMessage}`)
    } finally {
      setIsLoading(false)
    }
  }, [amount, activeTab, price, triggerPrice, triggerType, product, lastPrice, currentSymbol, fetchTickerData])
  
  const handleBuy = useCallback(async () => {
    await placeOrder('BUY')
  }, [placeOrder])

  const handleSell = useCallback(async () => {
    await placeOrder('SELL')
  }, [placeOrder])
  
  // Auto-trigger buy/sell if initialAction is provided
  // useEffect(() => {
  //   if (initialAction === 'BUY' && price) {
  //     handleBuy();
  //   } else if (initialAction === 'SELL' && price) {
  //     handleSell();
  //   }
  // }, [initialAction, price, handleBuy, handleSell]);



  return (
    <div className={styles.container}>
      {/* Header with Title and Symbol Selector */}
      <div className={styles.header}>
        <span className={styles.title}>Trade</span>
        <div className={styles.controls}>
                    
          {/* Display last price information for all tabs */}
          {lastPrice && (
            <div className={styles.lastPriceDisplay}>
              LTP: {lastPrice} {tickerData && tickerData.priceChangePercent !== undefined && (
                <span className={tickerData.priceChangePercent >= 0 ? styles.profit : styles.loss}>
                  ({tickerData.priceChangePercent >= 0 ? '+' : ''}{tickerData.priceChangePercent.toFixed(2)}%)
                </span>
              )}
            </div>
          )}
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <button 
              onClick={() => setIsSymbolPopupOpen(true)}
              className={styles.symbolSelect}
              aria-label="Select Symbol"
            >
              {currentSymbol.toUpperCase()} ▾
            </button>
            
            {isSymbolPopupOpen && (
              <SymbolSearchModal
                onSelect={handleSymbolChange}
                onClose={() => setIsSymbolPopupOpen(false)}
                title="Select Trading Symbol"
                recentStorageKey="zerodhaTradingSymbols"
                customPopularSymbols={popularSymbols}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Tab Header */}
      <div className={styles.tabHeader}>
        {TABS.map(t => (
          <div
            key={t}
            className={`${styles.tab} ${activeTab === t ? styles.activeTab : ''}`}
            onClick={() => {
              setActiveTab(t)
              if (t === 'Market') setPrice('')
            }}
          >
            {t}
          </div>
        ))}
      </div>
      <div className={styles.tabContent}>
        {/* Price input for Limit and SL orders */}
        {(activeTab === 'Limit' || activeTab === 'SL' || activeTab === 'GTT') && (
          <div className={styles.formRow}>
            <label>Price:</label>
            <input
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              placeholder="Enter limit price"
            />
          </div>
        )}
        
        {/* Trigger price input for SL, SL-M and GTT orders */}
        {(activeTab === 'SL' || activeTab === 'SL-M' || activeTab === 'GTT') && (
          <div className={styles.formRow}>
            <label>Trigger Price:</label>
            <div className={styles.triggerPriceContainer}>
              <input
                type="number"
                value={triggerPrice}
                onChange={e => setTriggerPrice(e.target.value)}
                placeholder="Enter trigger price"
              />
              {activeTab === 'GTT' && lastPrice && (
                <div className={styles.percentageButtons}>
                  <div>
                    <span>BUY:</span>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(1, 'BUY')}
                      className={percentageValue === 1 ? styles.activePercentage : ''}
                    >
                      +1%
                    </button>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(5, 'BUY')}
                      className={percentageValue === 5 ? styles.activePercentage : ''}
                    >
                      +5%
                    </button>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(10, 'BUY')}
                      className={percentageValue === 10 ? styles.activePercentage : ''}
                    >
                      +10%
                    </button>
                  </div>
                  <div>
                    <span>SELL:</span>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(1, 'SELL')}
                      className={percentageValue === 1 ? styles.activePercentage : ''}
                    >
                      -1%
                    </button>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(5, 'SELL')}
                      className={percentageValue === 5 ? styles.activePercentage : ''}
                    >
                      -5%
                    </button>
                    <button 
                      type="button" 
                      onClick={() => updateTriggerPriceFromPercentage(10, 'SELL')}
                      className={percentageValue === 10 ? styles.activePercentage : ''}
                    >
                      -10%
                    </button>
                  </div>
                </div>
              )}
            </div>
            {activeTab === 'GTT' && lastPrice && (
              <div className={styles.lastPriceInfo}>
                LTP: {lastPrice} {tickerData && tickerData.priceChangePercent !== undefined && (
                  <span className={tickerData.priceChangePercent >= 0 ? styles.profit : styles.loss}>
                    ({tickerData.priceChangePercent >= 0 ? '+' : ''}{tickerData.priceChangePercent.toFixed(2)}%)
                  </span>
                )} | Trigger at: {triggerPrice}
                <div className={styles.triggerPriceHint}>
                  For BUY orders: trigger price must be above current price.<br/>
                  For SELL orders: trigger price must be below current price.
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* GTT specific options */}
        {activeTab === 'GTT' && (
          <>
            <div className={styles.formRow}>
              <label>Trigger Type:</label>
              <select 
                value={triggerType} 
                onChange={e => setTriggerType(e.target.value as 'SINGLE' | 'OCO')}
                className={styles.selectInput}
              >
                <option value="SINGLE">Single</option>
                <option value="OCO">OCO (One Cancels Other)</option>
              </select>
            </div>
            <div className={styles.formRow}>
              <label>Product:</label>
              <select 
                value={product} 
                onChange={e => setProduct(e.target.value as 'CNC' | 'MIS' | 'NRML')}
                className={styles.selectInput}
              >
                <option value="CNC">CNC (Delivery)</option>
                <option value="MIS">MIS (Intraday)</option>
                <option value="NRML">NRML (Normal)</option>
              </select>
            </div>
          </>
        )}
        
        <div className={styles.formRow}>
          <label>Amount:</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Enter amount"
          />
        </div>
        <div className={styles.buttons}>
          <button 
            className={styles.buyButton} 
            onClick={handleBuy} 
            disabled={isLoading || !amount || 
              (activeTab === 'Limit' && !price) || 
              (activeTab === 'SL' && (!price || !triggerPrice)) || 
              (activeTab === 'SL-M' && !triggerPrice) ||
              (activeTab === 'GTT' && !triggerPrice)}
          >
            {isLoading ? 'Processing...' : 'Buy'}
          </button>
          <button 
            className={styles.sellButton} 
            onClick={handleSell}
            disabled={isLoading || !amount || 
              (activeTab === 'Limit' && !price) || 
              (activeTab === 'SL' && (!price || !triggerPrice)) || 
              (activeTab === 'SL-M' && !triggerPrice) ||
              (activeTab === 'GTT' && !triggerPrice)}
          >
            {isLoading ? 'Processing...' : 'Sell'}
          </button>
        </div>
        
        {orderStatus && (
          <div className={`${styles.orderStatus} ${orderStatus.includes('failed') || orderStatus.includes('Please fill') ? styles.error : styles.success}`}>
            {orderStatus}
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(BuySellComponent)
